import { db } from '../firebase';
import { collection, getDocs } from 'firebase/firestore';
import { normalizePicks } from './normalizePicks';

// Weekly score computation — the single scoring algorithm for BigBoard.
//
// HAND-MIRRORED with functions/lib/scoring.js (computeWeeklyScores): this copy
// drives the admin "Calculate & Preview" table, the functions copy is what the
// publishScores callable actually publishes. Any scoring-rule change must be
// made in BOTH files (same situation as normalizePicks.js and
// notificationEligibility.js).
//
// Publishing itself is server-side only: security rules deny all direct client
// writes to /picks (players go through submitPicks), so the old client-side
// publishScoresToLeaderboard could never commit its transaction. The admin UI
// calls the publishScores callable instead.

/**
 * Pure computation: no Firestore access. Mutates the pick objects inside the
 * returned userPicks (flag stamping) — the server write-back persists that.
 *
 * @param {Array<Object>} games - Game docs as [{ id, ...data }].
 * @param {Array<Object>} rawUserPicks - Pick docs as [{ userId, ...data }],
 *   where userId is the doc id (the user's email).
 * @returns {{ weeklyScores: Object, winningTeams: Object, userPicks: Array }}
 */
export const computeWeeklyScores = (games, rawUserPicks) => {
  // Bridge legacy array-shaped picks to the gameId-keyed map so the lookups
  // below — and the publish write-back — key by game.id.
  const userPicks = rawUserPicks.map((u) => ({
    ...u,
    picks: normalizePicks(u.picks, games),
  }));

  // Validation: every game must have a final score
  for (const game of games) {
    if (game.homeScore === undefined || game.awayScore === undefined) {
      throw new Error(
        `Game ${game.id} is missing a final score. Please enter all scores before calculating.`
      );
    }
  }

  const weeklyScores = {};
  const winningTeams = {};

  // Initialize scores for all users who made picks
  userPicks.forEach((pick) => {
    weeklyScores[pick.userId] = {
      pickWinnerPoints: 0,
      pickScorePoints: 0,
      total: 0,
      name: pick.name || pick.userId,
    };
  });

  for (const game of games) {
    // Ensure scores are numbers
    const homeScore = Number(game.homeScore);
    const awayScore = Number(game.awayScore);

    if (isNaN(homeScore) || isNaN(awayScore)) {
      console.warn(`Skipping game ${game.id} due to invalid scores.`);
      continue; // Skip games that haven't been scored yet
    }

    const actualWinner = homeScore > awayScore ? game.homeTeam : game.awayTeam;
    winningTeams[game.id] = actualWinner;

    const eligiblePicks = [];

    for (const user of userPicks) {
      const pick = user.picks[game.id];
      if (!pick) continue; // User didn't make a pick for this game

      if (game.isPickScore) {
        // Reset stale flags from a prior publish so a score correction can
        // un-award a winning pick; merge writes never delete omitted keys.
        pick.isWinningPick = false;
        pick.isExactMatch = false;
      }

      let pickedWinner = null;

      // Determine the winner from the user's pick
      if (pick.homeScore !== undefined && pick.awayScore !== undefined) {
        // This is a "Pick the Score" type of pick
        const awayDiff = Math.abs(Number(pick.awayScore) - awayScore);
        const homeDiff = Math.abs(Number(pick.homeScore) - homeScore);
        pick.scoreDifference = awayDiff + homeDiff; // Store difference for all picks

        if (Number(pick.homeScore) > Number(pick.awayScore)) {
          pickedWinner = game.homeTeam;
        } else if (Number(pick.awayScore) > Number(pick.homeScore)) {
          pickedWinner = game.awayTeam;
        }
      } else {
        // This is a "Pick the Winner" type of pick
        if (pick.homeWin) {
          pickedWinner = game.homeTeam;
        } else if (pick.awayWin) {
          pickedWinner = game.awayTeam;
        }
      }

      // Now, check if the picked winner matches the actual winner
      if (pickedWinner === actualWinner) {
        if (!game.isPickScore || game.awardWinnerBonus) {
          // Award winner bonus for Pick Winner games, or Pick Score games with bonus enabled
          weeklyScores[user.userId].pickWinnerPoints += 0.25;
        }
        if (game.isPickScore) {
          // If it's a "Pick the Score" game and the winner is correct, they are eligible for score points
          eligiblePicks.push({
            userId: user.userId,
            pick,
          });
        }
      }
    }

    if (game.isPickScore && eligiblePicks.length > 0) {
      let exactMatches = [];
      let closestDiff = Infinity;
      let closestPicks = [];

      // Check for exact score matches first
      for (const { userId, pick } of eligiblePicks) {
        if (
          Number(pick.homeScore) === homeScore &&
          Number(pick.awayScore) === awayScore
        ) {
          exactMatches.push(userId);
        }
      }

      if (exactMatches.length > 0) {
        // Award 3 points to all exact matches
        exactMatches.forEach((userId) => {
          weeklyScores[userId].pickScorePoints += 3;
          const userPick = userPicks.find((p) => p.userId === userId);
          if (userPick) {
            const pick = userPick.picks[game.id];
            pick.isWinningPick = true;
            pick.isExactMatch = true;
          }
        });
      } else {
        // If no exact matches, find the closest score by summing the difference of each score
        for (const { userId, pick } of eligiblePicks) {
          if (pick.homeScore !== undefined && pick.awayScore !== undefined) {
            if (pick.scoreDifference < closestDiff) {
              closestDiff = pick.scoreDifference;
              closestPicks = [userId];
            } else if (pick.scoreDifference === closestDiff) {
              closestPicks.push(userId);
            }
          }
        }
        // Award 1 point to all closest picks
        closestPicks.forEach((userId) => {
          weeklyScores[userId].pickScorePoints += 1;
          const userPick = userPicks.find((p) => p.userId === userId);
          if (userPick) {
            const pick = userPick.picks[game.id];
            pick.isWinningPick = true;
            pick.isExactMatch = false;
          }
        });
      }
    }
  }

  // Calculate total points for each user
  for (const userId in weeklyScores) {
    weeklyScores[userId].total =
      weeklyScores[userId].pickWinnerPoints +
      weeklyScores[userId].pickScorePoints;
  }

  return { weeklyScores, winningTeams, userPicks };
};

/**
 * Calculates the scores for a given week and returns the results without
 * writing to the database (used for the admin preview).
 *
 * @param {string} year - The year of the season (e.g., "2024").
 * @param {string} week - The week number.
 * @returns {Promise<Object>} An object containing the calculated points for each user.
 */
export const calculateWeeklyScores = async (year, week) => {
  const gamesRef = collection(db, 'games', year, week.toString());
  const picksRef = collection(db, 'picks', year, week.toString());

  const [gamesSnapshot, picksSnapshot] = await Promise.all([
    getDocs(gamesRef),
    getDocs(picksRef),
  ]);

  if (gamesSnapshot.empty) {
    throw new Error(`No games found for year ${year}, week ${week}.`);
  }

  const games = gamesSnapshot.docs.map((doc) => ({
    id: doc.id,
    ...doc.data(),
  }));
  const userPicks = picksSnapshot.docs.map((doc) => ({
    userId: doc.id,
    ...doc.data(),
  }));

  return computeWeeklyScores(games, userPicks);
};
